<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { twMerge } from 'tailwind-merge'
import { formatDuration } from '@/helpers/date'
import SparkIcon from '@/components/icons/SparkIcon.vue'
import type { HomeButtonProps } from './HomeButton.types'

const props = withDefaults(defineProps<HomeButtonProps>(), {
  animation: 'none',
  pulseShadowColor: 'pink',
  showSparkle: false,
  size: undefined,
  borderRadius: undefined,
  containerClass: '',
  buttonClass: '',
  backgroundClass: '',
  variant: 'side',
  noBorder: false,
})

const slots = useSlots()

// Derive size from variant when not explicitly provided
const effectiveSize = computed(() => {
  if (props.size) return props.size
  return props.variant === 'bottom' ? 'size-[40px]' : 'size-[52px]'
})

// Derive border radius from variant when not explicitly provided
const effectiveBorderRadius = computed(() => {
  if (props.borderRadius) return props.borderRadius
  return props.variant === 'bottom' ? 'rounded-[10px]' : 'rounded-[13px]'
})

// Default danger thresholds in seconds
const DEFAULT_DANGER_THRESHOLD = 3600 * 12 // 12 hours

// Computed classes for the button container
const buttonContainerClass = computed(() => {
  const classes: string[] = [
    `relative flex items-center justify-center ${props.noBorder ? '' : props.variant === 'bottom' ? 'white-border-thin' : 'white-border'}`,
    effectiveSize.value,
    effectiveBorderRadius.value,
    props.backgroundClass,
  ]

  // Add animation classes
  if (props.animation === 'scale-up') {
    classes.push('scale-up-button')
  } else if (props.animation === 'pulse-shadow') {
    classes.push(pulseShadowClass.value)
  }

  return twMerge(classes.join(' '), props.buttonClass)
})

// Get pulse shadow class based on color
const pulseShadowClass = computed(() => {
  const colorMap: Record<string, string> = {
    pink: 'pulse-shadow',
    green: 'pulse-shadow-default-discount',
    orange: 'pulse-shadow-default-BOGO',
    blue: 'pulse-shadow-cashback',
    gold: 'pulse-shadow',
  }
  return colorMap[props.pulseShadowColor] || 'pulse-shadow'
})

// Countdown formatting
const formattedCountdown = computed(() => {
  if (!props.countdown) return ''
  const showDays = props.countdown.showDays ?? props.countdown.value > 3600 * 72
  return formatDuration(props.countdown.value, undefined, showDays)
})

// Whether countdown is in danger zone
const isCountdownDanger = computed(() => {
  if (!props.countdown) return false
  const threshold = props.countdown.dangerThreshold ?? DEFAULT_DANGER_THRESHOLD
  return props.countdown.value < threshold
})

// Check if we have a label to show
const hasLabel = computed(() => {
  return props.label || slots['label-custom']
})

// Check if we have countdown to show
const hasCountdown = computed(() => {
  return props.countdown && props.countdown.value > 0
})

// Check if we have a counter badge
const hasCounterBadge = computed(() => {
  return props.counterBadge && props.counterBadge.count > 0
})
</script>

<template>
  <div :class="twMerge('relative flex flex-col items-center justify-center', containerClass)">
    <!-- Button Container -->
    <div
      :class="buttonContainerClass"
      :style="containerStyle"
    >
      <!-- Badge Top Left Slot (for DefaultBadge, sale badges) -->
      <slot name="badge-top-left" />

      <!-- Counter Badge (top-right) - auto-rendered if counterBadge prop provided -->
      <div
        v-if="hasCounterBadge && !$slots['badge-top-right']"
        class="absolute size-4 -top-1 -right-1 bg-teal-primary rounded-full pulse-shadow-sm-scale-up flex items-center justify-center z-[5]"
      >
        <span class="text-tropee-black text-[10px] font-semibold">
          {{ counterBadge!.count }}
        </span>
      </div>
      <!-- Badge Top Right Slot (for custom counter badges) -->
      <slot name="badge-top-right" />

      <!-- Badge Custom Slot (for PathwayEvent-style custom badges) -->
      <slot name="badge-custom" />

      <!-- Blur Effect Slot (colored blur behind icon) -->
      <slot name="blur-effect" />

      <!-- Sparkle Star Icon -->
      <SparkIcon
        v-if="showSparkle"
        class="absolute sparkling-star h-4 w-4 z-[10]"
      />

      <!-- Default Slot (main icon content - GIF, Lottie, images) -->
      <slot />
      <!-- Overlay Slot (additional decorations like stars) -->
      <slot name="overlay" />
    </div>

    <!-- Label Section -->
    <div
      v-if="hasLabel || hasCountdown"
      class="z-[10] flex flex-col items-center min-w-[54px] top-full -mt-1.5 -tracking-[0.5px] uppercase"
    >
        <!-- Main Label -->
        <div
          v-if="hasLabel && !$slots['label-custom']"
          class="font-black text-[10px] leading-[10px] black-stroke-white-text-2 text-center text-[#3A3A3C] min-w-[54px] break-word line-clamp-2"
          style="word-spacing: 100vw"
        >
          {{ label }}
        </div>

        <!-- Custom Label Slot -->
        <slot name="label-custom" />

        <!-- Countdown Label -->
        <div
          v-if="hasCountdown"
          class="font-extrabold text-[10px] leading-[14px] tabular-nums black-stroke-3 min-w-[54px] text-center -mt-1"
          :class="isCountdownDanger ? 'text-[#e8405f]' : 'text-yellow-300'"
          style="font-feature-settings: 'ss02' on, 'ss03' on, 'ss04' on"
        >
          {{ formattedCountdown }}
        </div>
    </div>
  </div>
</template>

<style scoped>
/* Sparkle animation for the star icon */
.sparkling-star {
  top: 4px;
  right: 4px;
  animation: sparkle 2.5s infinite;
  transition: all;
  transition-delay: 200ms;
}

@keyframes sparkle {
  0% {
    bottom: auto;
    left: auto;
    top: 4px;
    right: 4px;
    opacity: 1;
  }
  20% {
    bottom: auto;
    left: auto;
    top: 4px;
    right: 4px;
    opacity: 0;
  }
  35% {
    top: auto;
    right: auto;
    bottom: 4px;
    left: 4px;
    opacity: 0;
  }
  50% {
    top: auto;
    right: auto;
    bottom: 4px;
    left: 4px;
    opacity: 1;
  }
  70% {
    top: auto;
    right: auto;
    bottom: 4px;
    left: 4px;
    opacity: 0;
  }
  85% {
    bottom: auto;
    left: auto;
    top: 4px;
    right: 4px;
    opacity: 0;
  }
  100% {
    bottom: auto;
    left: auto;
    top: 4px;
    right: 4px;
    opacity: 1;
  }
}

/* Scale-up animation */
@keyframes scale-up-button {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.03);
  }
}

.scale-up-button {
  animation: scale-up-button 0.75s ease infinite alternate;
  will-change: transform;
}

/* Label styling from Figma */
.home-button-label {
  color: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 900;
  line-height: 14px;
  -webkit-text-stroke-width: 1px;
  -webkit-text-stroke-color: #3A3A3C;
  font-feature-settings: 'ss02' on, 'ss03' on, 'ss04' on;
}

/* Black stroke without changing text color */
.black-stroke-3 {
  -webkit-text-stroke-width: 3px;
  -webkit-text-stroke-color: #1D1D1B;
  paint-order: stroke fill;
}

/* White gradient border */
.white-border::before {
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.4) 0%,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 0.4) 100%
  );
  border-radius: inherit;
  content: "";
  inset: 0;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  padding: 2px;
  position: absolute;
  z-index: 1;
  pointer-events: none;
}
/* White gradient border - thin variant for bottom icons */
.white-border-thin::before {
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.4) 0%,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 0.4) 100%
  );
  border-radius: inherit;
  content: "";
  inset: 0;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  padding: 1px;
  position: absolute;
  z-index: 1;
  pointer-events: none;
}
.white-stroke-black-text-2 {
  -webkit-text-stroke-width: 2px;
  -webkit-text-stroke-color: white;
  paint-order: stroke fill;
}
.break-word {
  word-break: break-word;
}
.black-stroke-white-text-2 {
  -webkit-text-stroke-width: 1px !important;
}
</style>

<style lang="scss">
@import '@/assets/styles/sales.scss';
</style>
